import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, Pencil, Eraser, Save, Loader2, Undo2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Annotated image viewer for case chat.
 * - Loads the underlying image from GridFS — cookie auth is auto-attached on
 *   same-origin image fetches (Phase B, cookie-only).
 * - Shows existing strokes; lets the user draw new ones.
 * - Stroke colour is FIXED by role: blue (#1d4ed8) for Lab, green (#059669) for Clinic.
 * - Each stroke is sent to PUT /cases/{case_id}/messages/{message_id}/annotations on Save.
 */
export default function AnnotatedImage({ caseId, message, myRole, onClose, onSaved }) {
    const imgRef = useRef(null);
    const canvasRef = useRef(null);
    const overlayRef = useRef(null);
    const [imgLoaded, setImgLoaded] = useState(false);
    const [strokes, setStrokes] = useState(message.annotations || []);
    const [drawing, setDrawing] = useState(null);
    const [tool, setTool] = useState("pen"); // pen | eraser
    const [saving, setSaving] = useState(false);

    const myColor = myRole && myRole !== "client" ? "#1d4ed8" : "#059669";

    const imgUrl = `${API}/files/${message.image_id}`;

    // Redraw whenever strokes change
    useEffect(() => {
        const c = canvasRef.current;
        const img = imgRef.current;
        if (!c || !img || !imgLoaded) return;
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        const ctx = c.getContext("2d");
        ctx.clearRect(0, 0, c.width, c.height);
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        for (const s of strokes) {
            if (!s.points || s.points.length < 1) continue;
            ctx.strokeStyle = s.color || "#1d4ed8";
            ctx.lineWidth = s.width || 4;
            ctx.beginPath();
            ctx.moveTo(s.points[0][0], s.points[0][1]);
            for (let i = 1; i < s.points.length; i++) ctx.lineTo(s.points[i][0], s.points[i][1]);
            ctx.stroke();
        }
        // live in-progress stroke
        if (drawing) {
            ctx.strokeStyle = drawing.color;
            ctx.lineWidth = drawing.width;
            ctx.beginPath();
            ctx.moveTo(drawing.points[0][0], drawing.points[0][1]);
            for (let i = 1; i < drawing.points.length; i++) ctx.lineTo(drawing.points[i][0], drawing.points[i][1]);
            ctx.stroke();
        }
    }, [strokes, drawing, imgLoaded]);

    const getPos = (e) => {
        const rect = overlayRef.current.getBoundingClientRect();
        const sx = canvasRef.current.width / rect.width;
        const sy = canvasRef.current.height / rect.height;
        const x = ((e.touches?.[0]?.clientX ?? e.clientX) - rect.left) * sx;
        const y = ((e.touches?.[0]?.clientY ?? e.clientY) - rect.top) * sy;
        return [Math.round(x), Math.round(y)];
    };

    const startStroke = (e) => {
        if (!imgLoaded) return;
        e.preventDefault();
        if (tool === "eraser") {
            // Erase: drop any stroke whose bounding box touches the click point
            const [x, y] = getPos(e);
            const tol = 12;
            setStrokes((prev) => prev.filter((s) => {
                return !(s.points || []).some(([px, py]) => Math.abs(px - x) < tol && Math.abs(py - y) < tol);
            }));
            return;
        }
        setDrawing({ type: "stroke", color: myColor, width: 4, points: [getPos(e)] });
    };
    const moveStroke = (e) => {
        if (!drawing) return;
        e.preventDefault();
        setDrawing((d) => ({ ...d, points: [...d.points, getPos(e)] }));
    };
    const endStroke = () => {
        if (!drawing) return;
        if (drawing.points.length > 1) {
            setStrokes((prev) => [...prev, drawing]);
        }
        setDrawing(null);
    };

    const undo = () => setStrokes((prev) => prev.slice(0, -1));

    const save = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/cases/${caseId}/messages/${message.id}/annotations`, { annotations: strokes });
            toast.success("Annotations saved");
            onSaved?.(strokes);
            onClose?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="annotated-image-modal" onClick={onClose}>
            <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[94vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-3">
                    <div className="font-display text-[15px] text-slate-900">Annotate image</div>
                    <span className="text-[11.5px] font-mono-tr text-slate-500">{strokes.length} stroke{strokes.length === 1 ? "" : "s"}</span>
                    <div className="ms-auto flex items-center gap-1">
                        <button onClick={() => setTool("pen")} data-testid="tool-pen" title="Pen" className={`p-2 rounded-lg ${tool === "pen" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}><Pencil size={15} /></button>
                        <button onClick={() => setTool("eraser")} data-testid="tool-eraser" title="Eraser" className={`p-2 rounded-lg ${tool === "eraser" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}><Eraser size={15} /></button>
                        <button onClick={undo} data-testid="tool-undo" title="Undo" className="p-2 rounded-lg text-slate-600 hover:bg-slate-100"><Undo2 size={15} /></button>
                        <div className="mx-2 h-6 w-px bg-slate-200" />
                        <div className="flex items-center gap-1.5 text-[11.5px]">
                            <span className="inline-block w-3 h-3 rounded-full" style={{ background: myColor }} />
                            <span className="text-slate-600 font-medium">{myRole && myRole !== "client" ? "Lab · Blue" : "Clinic · Green"}</span>
                        </div>
                        <button onClick={save} disabled={saving} data-testid="annotation-save" className="ms-2 px-3.5 py-2 rounded-lg bg-cyan-600 text-white text-[12.5px] font-medium hover:bg-cyan-700 disabled:opacity-50 inline-flex items-center gap-1.5">
                            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
                        </button>
                        <button onClick={onClose} className="ms-1 p-2 text-slate-400 hover:text-slate-700"><X size={17} /></button>
                    </div>
                </div>

                <div className="flex-1 overflow-auto bg-slate-900/95 grid place-items-center p-4">
                    <div ref={overlayRef}
                        onMouseDown={startStroke} onMouseMove={moveStroke} onMouseUp={endStroke} onMouseLeave={endStroke}
                        onTouchStart={startStroke} onTouchMove={moveStroke} onTouchEnd={endStroke}
                        className="relative inline-block touch-none select-none" style={{ cursor: tool === "eraser" ? "cell" : "crosshair" }}>
                        <img ref={imgRef} src={imgUrl} alt="" onLoad={() => setImgLoaded(true)} className="max-w-full max-h-[70vh] block rounded-md" style={{ userSelect: "none" }} />
                        <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
                        {!imgLoaded && (
                            <div className="absolute inset-0 grid place-items-center text-white"><Loader2 className="animate-spin" /></div>
                        )}
                    </div>
                </div>

                <div className="px-5 py-2.5 border-t border-slate-200 bg-slate-50 text-[11.5px] text-slate-500 font-mono-tr flex items-center gap-2">
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#1d4ed8]" /> Lab annotations
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#059669] ml-3" /> Clinic annotations
                    <span className="ms-auto">Click + drag to draw · Eraser removes nearby strokes · Saved annotations are immutable in the audit log.</span>
                </div>
            </div>
        </div>
    );
}
